// CI job (arc 101 slice 07 — "Blacksmith competitor", doc 07). A customer's
// GitHub Actions `workflow_job` that a maintainer opted to run on the account's
// OWN hardware we orchestrate: change one line — `runs-on: developerz-4vcpu` —
// and the job runs on an enrolled box.
//
// Flow: the `workflow_job.queued` webhook carries the `runs-on` labels → the CI
// assigner (the sibling of the fleet task assigner, `apps/worker/src/dispatch/`)
// picks an eligible idle box (labels ↔ `runners.capabilities` + the per-account
// entitlement cap) → mints a one-shot JIT runner (`@developerz/github`) → ships
// ONLY the encoded jitconfig to the box over `fleet.task.<runnerId>`. App creds
// never touch customer hardware. One row per job in `ci_jobs`; GitHub's
// `workflow_job.id` is the natural idempotency key.
//
// This is the DOMAIN half: the durable status/conclusion vocabulary, the webhook
// slice we parse, the label→capability convention, and the wire contract for the
// box dispatch. Pure — no I/O (domain imports nothing internal).

import { z } from 'zod';
import {
  type FleetTaskAssignment,
  FleetTaskAssignmentSchema,
} from '../value-objects/fleet-task-wire';
import { AccountIdSchema } from '../value-objects/ids';

// ─── status + conclusion ─────────────────────────────────────────────────────

export const CiJobStatus = {
  /** Recorded from `workflow_job.queued`; not yet placed on a box. */
  Queued: 'queued',
  /** The assigner placed it on a box and shipped the jitconfig; the box hasn't started yet. */
  Assigned: 'assigned',
  /** The box's ephemeral runner picked the job up (`workflow_job.in_progress`). */
  Running: 'running',
  /** GitHub reported `workflow_job.completed`. Terminal — see {@link CiJobConclusion}. */
  Completed: 'completed',
  /**
   * We declined to place it on a box — a non-transient block the job can't outgrow by
   * waiting (see {@link CiRefusalReason}), so it leaves the `queued` pool for good rather
   * than being re-attempted every arbiter tick. Terminal (never re-queued: a GitHub re-run
   * mints a NEW `workflow_job.id` ⇒ a fresh row). Distinct from the transient gaps that stay
   * `queued` for a later pass (`no_capacity` — a bigger box helps; a network mint blip).
   */
  Refused: 'refused',
} as const;
export const CiJobStatusSchema = z.enum(CiJobStatus);
export type CiJobStatus = z.infer<typeof CiJobStatusSchema>;

/**
 * Why a {@link CiJobStatus.Refused} job was declined — the queryable reason on the row (so
 * the dashboard tells an App-re-acceptance issue apart from any other refusal). `permission_missing`
 * is the org that hasn't re-accepted the `organization_self_hosted_runners` permission: the
 * `generate-jitconfig` mint 403s, and no amount of retrying fixes it until a human re-accepts —
 * so the job is refused (not looped) and this reason is surfaced for the fix.
 */
export const CiRefusalReason = {
  PermissionMissing: 'permission_missing',
} as const;
export const CiRefusalReasonSchema = z.enum(CiRefusalReason);
export type CiRefusalReason = z.infer<typeof CiRefusalReasonSchema>;

/**
 * GitHub `workflow_job.conclusion` — set only once a job `completed`. Mirrors
 * GitHub's own vocabulary verbatim so a dashboard renders it without a second
 * mapping; validated at read (D3 no-pgEnum stance, same as `runners.status`).
 */
export const CiJobConclusion = {
  Success: 'success',
  Failure: 'failure',
  Neutral: 'neutral',
  Cancelled: 'cancelled',
  Skipped: 'skipped',
  TimedOut: 'timed_out',
  ActionRequired: 'action_required',
  Stale: 'stale',
} as const;
export const CiJobConclusionSchema = z.enum(CiJobConclusion);
export type CiJobConclusion = z.infer<typeof CiJobConclusionSchema>;

// ─── safety context ──────────────────────────────────────────────────────────

/**
 * The triggering workflow event, as far as the CI dispatch pipeline resolved it.
 * `pull_request_target` is the load-bearing one — a workflow that runs with the
 * BASE repo's secrets + write token; combined with a fork head it is the classic
 * secret-exfiltration vector (untrusted fork code + privileged context). `other`
 * is the honest "not one we special-case" bucket.
 */
export const CiTriggerEvent = {
  Push: 'push',
  PullRequest: 'pull_request',
  PullRequestTarget: 'pull_request_target',
  Other: 'other',
} as const;
export const CiTriggerEventSchema = z.enum(CiTriggerEvent);
export type CiTriggerEvent = z.infer<typeof CiTriggerEventSchema>;

/**
 * Map GitHub's raw `workflow_run.event` string to our {@link CiTriggerEvent}. Only the
 * three events we gate on survive; everything else (`schedule`, `workflow_dispatch`,
 * `release`, …) folds to `Other` — the honest "not a vector we special-case" bucket.
 */
export function toCiTriggerEvent(raw: string): CiTriggerEvent {
  switch (raw) {
    case CiTriggerEvent.Push:
    case CiTriggerEvent.PullRequest:
    case CiTriggerEvent.PullRequestTarget:
      return raw;
    default:
      return CiTriggerEvent.Other;
  }
}

/**
 * The security context a box needs to run a CI job SAFELY on customer hardware —
 * shipped on the dispatch, since the box has no installation token to resolve it
 * itself (app creds stay in the control plane). The box enforces two rules from it
 * (`apps/runner` `ci-exec.ts`): refuse the fork-PR + `pull_request_target` exfil
 * vector outright ({@link ciDispatchExfilRisk}), and run a PUBLIC repo's untrusted
 * code under a filtered-egress network. Optional on the wire (same posture as
 * `FleetTaskAssignment.outcome_kind`): an omitted context ⇒ the box's conservative
 * default (treat as public/egress-filtered, refuse only on a positive exfil signal).
 */
export const CiSafetyContextSchema = z.object({
  /** The event that triggered the workflow (`pull_request_target` is the risky one). */
  event: CiTriggerEventSchema,
  /** The job's head repo is a FORK of the base — untrusted-contributor code. */
  head_repo_fork: z.boolean(),
  /** The repo is PUBLIC — untrusted contributors can open PRs, so egress is filtered. */
  repo_public: z.boolean(),
});
export type CiSafetyContext = z.infer<typeof CiSafetyContextSchema>;

/**
 * The exfiltration vector: a FORK PR that triggers a `pull_request_target` workflow.
 * Such a run executes untrusted contributor code while GitHub grants it the BASE
 * repo's secrets + a write token — self-hosting it on a customer box would hand fork
 * code the account's secrets. Always refused (pure invariant; the control-plane
 * pre-check (`ci-assigner.ts`, pre-placement) AND the box gate (`ci-exec.ts`) both
 * call it — defense in depth).
 */
export function ciDispatchExfilRisk(safety: CiSafetyContext): boolean {
  return safety.event === CiTriggerEvent.PullRequestTarget && safety.head_repo_fork;
}

/**
 * A FORK-head pull request — untrusted contributor code. A fork PR's workflow runs code
 * from OUTSIDE the base repo's trust boundary, so self-hosting it would execute
 * attacker-controllable code on the maintainer's fleet hardware. Such a job NEVER runs on
 * a box — REGARDLESS of the trigger event (`pull_request` OR `pull_request_target`) or repo
 * visibility, with NO opt-in key (`apps/marketing/content/docs/ci.mdx` "Fork PR policy").
 * A SUPERSET of {@link ciDispatchExfilRisk} (the narrower fork + `pull_request_target`
 * secrets-exfil sub-case): both the control-plane pre-check (`ci-assigner.ts`, pre-placement)
 * and the box gate (`ci-exec.ts`) refuse on it (defense in depth). `head_repo_fork` is only
 * ever set for a fork PR, so the flag alone is the signal — and fail-closed if a shape drift
 * ever set it on a non-PR run.
 */
export function ciDispatchForkPr(safety: CiSafetyContext): boolean {
  return safety.head_repo_fork === true;
}

// ─── the durable row ─────────────────────────────────────────────────────────

/**
 * A normalized `ci_jobs` row. `githubJobId` (GitHub's `workflow_job.id`) is the
 * natural idempotency key — a redelivered `queued` collides on it. `runnerId` is
 * the box the job was placed on (null until assigned). `logRef` / `logExcerpt`
 * are filled by the log sink (slice 07 PR 18) — the R2/S3 object key and the
 * inline `<2KB` failure tail; null until a job's logs land.
 */
export const CiJobSchema = z.object({
  /** Our internal uuid PK (a DB handle, not a domain value object). */
  id: z.string().uuid(),
  accountId: AccountIdSchema,
  /** `owner/name` — the repo whose Actions job this is. */
  repoFullName: z.string().min(1),
  /** GitHub's `workflow_job.id` — the idempotency key. */
  githubJobId: z.number().int(),
  /** GitHub's parent `workflow_run` id; for correlation. Null when the webhook omitted it. */
  githubRunId: z.number().int().nullable(),
  /** The job's `runs-on` labels verbatim (e.g. `['developerz-4vcpu']`). */
  labels: z.array(z.string()),
  /**
   * The {@link CiSafetyContext} the assigner resolved from the parent `workflow_run`
   * at queue time (arc 101 slice 02) and the box enforces before running. Null on a row
   * queued before the resolution landed — the box then fails closed (absent context ⇒
   * refuse). A risky fork-PR + `pull_request_target` job is refused pre-placement, so a
   * live `queued` row always carries a resolved, non-risk context.
   */
  safety: CiSafetyContextSchema.nullable(),
  status: CiJobStatusSchema,
  /** The box (`runners.id`) the job was placed on; null until assigned. */
  runnerId: z.string().nullable(),
  /** GitHub's terminal conclusion; null until `completed`. */
  conclusion: CiJobConclusionSchema.nullable(),
  /** Why the job was {@link CiJobStatus.Refused}; null unless `status === 'refused'`. */
  refusalReason: CiRefusalReasonSchema.nullable(),
  /** Object key of the archived log in R2 / BYO-S3 (slice 07 PR 18); null until logs land. */
  logRef: z.string().nullable(),
  /** Inline `<2KB` failure-tail excerpt for queryability (slice 07 PR 18); null until logs land. */
  logExcerpt: z.string().nullable(),
  /** When GitHub queued the job (from the webhook); the pickup-latency baseline. */
  queuedAt: z.date().nullable(),
  /** When the assigner placed it on a box. */
  assignedAt: z.date().nullable(),
  /** When the box's runner started executing (`workflow_job.in_progress`). */
  startedAt: z.date().nullable(),
  /** When GitHub reported completion. */
  completedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CiJob = z.infer<typeof CiJobSchema>;

// ─── the `workflow_job` webhook slice ────────────────────────────────────────

/** GitHub `workflow_job.action`. `waiting` = deployment-gate hold; treated as a no-op. */
export const WorkflowJobAction = {
  Queued: 'queued',
  InProgress: 'in_progress',
  Completed: 'completed',
  Waiting: 'waiting',
} as const;
export const WorkflowJobActionSchema = z.enum(WorkflowJobAction);
export type WorkflowJobAction = z.infer<typeof WorkflowJobActionSchema>;

/**
 * The slice of a `workflow_job` webhook the CI assigner reads back from
 * `inbound_events` (the assigner re-reads the canonical body, never trusts the
 * queued metadata). Defensive `.catch()` on soft fields so a shape drift degrades
 * to a null / empty rather than throwing — the load-bearing fields (`action`, the
 * job `id`, the repo) are strict, everything else is best-effort.
 */
export const WorkflowJobEventSchema = z.object({
  action: WorkflowJobActionSchema,
  repository: z.object({
    full_name: z.string().min(1),
    /**
     * Repo visibility — the box's egress posture depends on it: a PUBLIC repo runs
     * untrusted contributor code, so its CI container joins a filtered-egress network
     * ({@link CiSafetyContext}). Soft (`.catch(null)`): GitHub always sends both, but a
     * shape drift degrades to null (the assigner then defaults conservatively) rather
     * than dropping the whole event.
     */
    private: z.boolean().nullish().catch(null),
    visibility: z.enum(['public', 'private', 'internal']).nullish().catch(null),
  }),
  workflow_job: z.object({
    id: z.number().int(),
    run_id: z.number().int().nullish().catch(null),
    name: z.string().catch(''),
    labels: z.array(z.string()).catch([]),
    /** Unknown / absent conclusions (still-running jobs) collapse to null. */
    conclusion: CiJobConclusionSchema.nullish().catch(null),
    started_at: z.string().nullish().catch(null),
    completed_at: z.string().nullish().catch(null),
  }),
  installation: z.object({ id: z.number().int() }).nullish().catch(null),
});
export type WorkflowJobEvent = z.infer<typeof WorkflowJobEventSchema>;

/**
 * The slice of GitHub's `GET /repos/{owner}/{repo}/actions/runs/{run_id}` the CI assigner
 * reads to resolve the fork-PR safety signal a `workflow_job` webhook cannot carry: the
 * triggering `event` and whether the run's HEAD repo is a fork (untrusted-contributor
 * code). `head_repository` is absent on a non-PR run (`push` / `schedule`), so a missing /
 * null fork flag reads as "not a fork". `event` is STRICT — a run body missing it is a
 * shape the assigner must not silently green-light, so the parse throws and the caller
 * fails closed (treat the fetch as failed ⇒ refuse); `head_repository.fork` is soft.
 */
export const WorkflowRunSchema = z.object({
  event: z.string(),
  head_repository: z
    .object({ fork: z.boolean().nullish().catch(null) })
    .nullish()
    .catch(null),
});

/**
 * Parse a GitHub ISO-8601 timestamp string to a `Date`, or null when it is
 * absent / unparseable — GitHub's `started_at` / `completed_at`. Never throws:
 * a garbled timestamp becomes null (a missing timing), not a dropped event.
 */
export function parseGithubTimestamp(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
}

// ─── label ↔ capability convention ───────────────────────────────────────────

/** Every developerz self-hosted runner label starts with this: `runs-on: developerz-4vcpu`. */
export const DEVELOPERZ_LABEL_PREFIX = 'developerz-' as const;

/**
 * The canonical developerz size-label grammar — the SINGLE source of truth shared by
 * {@link parseCiSizeLabel} (reads `N` from the named capture group) and
 * {@link ciRequiredCapabilities} (drops size labels from the hard-capability set), so
 * parse and assignment can never disagree on what a size label is. A label is a size
 * request iff it matches: the bare alias `developerz` (≡ 4 vCPU) or `developerz-{N}vcpu`
 * with `N` a positive integer (`[1-9][0-9]*` — no leading zero, no OS/arch suffix; boxes
 * are Ubuntu x64 by the enrollment contract). Group `n` is `N`, absent for the bare alias.
 */
const CI_SIZE_LABEL = /^developerz(?:-(?<n>[1-9][0-9]*)vcpu)?$/;

/** The bare `developerz` alias resolves to this many vCPUs (Blacksmith parity). */
const CI_BARE_LABEL_VCPUS = 4;

/** Blacksmith's memory ratio: every requested vCPU carries 4 GiB of RAM. */
const CI_MEM_BYTES_PER_VCPU = 4 * 1024 ** 3;

/**
 * A label that ENTERS the size namespace (`developerz-` then a digit, or anything
 * ending in the `vcpu` unit) but fails {@link CI_SIZE_LABEL} — a malformed size request:
 * zero / leading-zero / non-integer `N`, a wrong unit token, or an extra suffix. Such a
 * job is refused (`parseCiSizeLabel` ⇒ `'not_ours'`) rather than guessed at. Word-prefixed
 * feature labels (a future `developerz-docker`) are neither digit-prefixed nor
 * `vcpu`-suffixed, so they are NOT caught here — they flow through `ciRequiredCapabilities`
 * as hard capability tokens instead.
 */
const CI_SIZE_LABEL_SHAPE = /^developerz-(?:\d|.*vcpu$)/;

/**
 * Does this job target OUR runners? True iff it carries any developerz label. A
 * job with only GitHub-hosted labels (`ubuntu-latest`) is not ours — the assigner
 * ignores it, never records a `ci_jobs` row for it.
 */
export function isDeveloperzCiJob(labels: readonly string[]): boolean {
  return labels.some((l) => l.startsWith(DEVELOPERZ_LABEL_PREFIX));
}

/**
 * The capability tokens a box must satisfy to run this job (matched against a
 * box's `provisions` by `capabilitiesSatisfy`, the same primitive the fleet task
 * assigner uses). v1: every developerz label EXCEPT VM-size labels — a size label is
 * NOT a capability token here; box sizing is resolved separately by {@link parseCiSizeLabel}
 * against `runners.specs`, while a feature label (a future `developerz-docker`) IS a hard
 * requirement. So a plain `runs-on: developerz-4vcpu` yields NO required tokens → any
 * opted-in idle `ci` box of sufficient size may serve it; the `ci` opt-in + precedence is
 * enforced separately by `admitCiBackfill` (`dispatch/select.ts`).
 */
export function ciRequiredCapabilities(labels: readonly string[]): string[] {
  return labels.filter((l) => l.startsWith(DEVELOPERZ_LABEL_PREFIX) && !CI_SIZE_LABEL.test(l));
}

/**
 * Resolve a job's `runs-on` labels to the MINIMUM box size it must be placed on — the
 * single source the CI assigner (`apps/worker/src/dispatch`) and its demand read share.
 *
 * - `{ minCpus, minMemBytes }` — the job carries a developerz size label; a box may serve
 *   it only with AT LEAST this many vCPUs and bytes of RAM (Blacksmith ratio, 4 GiB per
 *   vCPU). The bare `developerz` alias is 4 vCPU / 16 GiB. When several size labels are
 *   present the LARGEST wins — a box that satisfies the max satisfies them all.
 * - `'unsized'` — no developerz size label at all (`ubuntu-latest`, or a pure feature label
 *   like a future `developerz-docker`): size is unconstrained, so any capability-matching
 *   idle box may serve it.
 * - `'not_ours'` — a label enters the size namespace but breaks the grammar (zero /
 *   leading-zero / non-integer `N`, wrong unit, extra suffix). We REFUSE the whole job,
 *   fail-closed: GitHub only dispatches to a runner advertising ALL requested labels, so
 *   claiming a size we can't honor would just fail the job. One malformed label poisons the
 *   set even beside a valid size.
 *
 * `minMemBytes` is raw bytes to match `runners.specs.memBytes` (`BoxSpecs`) directly — no
 * unit hops between parse and the specs comparison.
 */
export function parseCiSizeLabel(
  labels: readonly string[],
): { minCpus: number; minMemBytes: number } | 'unsized' | 'not_ours' {
  // Fail-closed: a single malformed size label poisons the whole job.
  if (labels.some((l) => CI_SIZE_LABEL_SHAPE.test(l) && !CI_SIZE_LABEL.test(l))) {
    return 'not_ours';
  }
  let maxCpus: number | null = null;
  for (const label of labels) {
    const match = CI_SIZE_LABEL.exec(label);
    if (!match) continue;
    // Group `n` is `N`; absent ⇒ the bare `developerz` alias (≡ 4 vCPU).
    const raw = match.groups?.['n'];
    const n = raw === undefined ? CI_BARE_LABEL_VCPUS : Number(raw);
    if (maxCpus === null || n > maxCpus) maxCpus = n;
  }
  if (maxCpus === null) return 'unsized';
  return { minCpus: maxCpus, minMemBytes: maxCpus * CI_MEM_BYTES_PER_VCPU };
}

// ─── box-dispatch wire contract ──────────────────────────────────────────────

/**
 * Wire contract for a CI-job dispatch on `fleet.task.<runner_id>` (arc 101 slice
 * 07) — the durable work item the CI assigner ships to a box's pull consumer, the
 * CI sibling of `FleetTaskAssignment`. Discriminated by `type` so the ONE
 * `fleet.task` consumer branches: `ci_dispatch` → jit-exec (launch
 * `run.sh --jitconfig` in an ephemeral container, one job, wipe — the box side is
 * `apps/runner` `ci-exec.ts`); a payload with no `type` is a coding `FleetTaskAssignment`.
 * `encoded_jit_config` is the ONLY secret shipped — the App creds and installation
 * token stay in the control plane. snake_case: crosses NATS verbatim, same
 * convention as `FleetTaskAssignment`.
 */
export const FleetCiDispatchSchema = z.object({
  type: z.literal('ci_dispatch'),
  /** Our `ci_jobs.id` — the box streams events / correlates its log back on it. */
  ci_job_id: z.string().uuid(),
  repo_full_name: z.string().min(1),
  /** The `runs-on` labels to register the one-shot JIT runner with. */
  labels: z.array(z.string()),
  /** The GitHub-minted, single-use encoded JIT config; the box runs `run.sh --jitconfig <blob>`. */
  encoded_jit_config: z.string().min(1),
  /**
   * The security context the box enforces before running (refuse the fork-PR +
   * `pull_request_target` exfil vector; egress-filter a public repo's untrusted
   * code). Optional so pre-slice / minimal producers still validate — an omitted
   * context ⇒ the box's conservative default (see `ci-exec.ts` `assessCiDispatch`).
   */
  safety: CiSafetyContextSchema.optional(),
});
export type FleetCiDispatch = z.infer<typeof FleetCiDispatchSchema>;

// ─── fleet.task.<runner_id> work item (union) ────────────────────────────────

/**
 * Everything that can land on `fleet.task.<runner_id>`: a coding `FleetTaskAssignment`
 * (no `type`) OR a `FleetCiDispatch` (`type: 'ci_dispatch'`). The box's ONE pull
 * consumer decodes THIS union and branches on `isCiDispatch` — a CI job runs the
 * ephemeral jit-exec path (`apps/runner` `ci-exec.ts`), everything else the coding
 * loop. `FleetCiDispatchSchema` is tried first: it requires the `type` literal, so a
 * coding assignment (which never carries it) falls through to the assignment member.
 */
export const FleetWorkItemSchema = z.union([FleetCiDispatchSchema, FleetTaskAssignmentSchema]);
export type FleetWorkItem = FleetCiDispatch | FleetTaskAssignment;

/** Discriminate a pulled work item: `true` ⇒ a CI jit-exec dispatch, `false` ⇒ a coding assignment. */
export function isCiDispatch(item: FleetWorkItem): item is FleetCiDispatch {
  return (item as { type?: unknown }).type === 'ci_dispatch';
}

// ─── ci-log report (box → control-plane, arc 101 slice 03a) ──────────────────────

/**
 * What the box reports upstream once it has finished archiving a CI job's console log
 * (the "write-half" of the CI-log pipeline). Rides `fleet.evt.<runId>.ci_log` — the
 * box's only publish grant — as the `payload` of a task-less {@link FleetRunEvent}
 * (`kind: 'ci_log'`), keyed to `ci_job_id` (NOT the box-minted run id).
 *
 * EXACTLY ONE of two shapes per finished job, and a **strict** union so the two can
 * never overlap (extra keys are rejected — a `{log_ref, upload_failed}` frankenpayload
 * fails BOTH arms):
 *   - archive SUCCEEDED → `{ ci_job_id, log_ref, excerpt }` — the mothership stamps
 *     `ci_jobs.log_ref` (its own derived key, never the wire `log_ref`) + `log_excerpt`
 *     and audits `CiLogRecorded`.
 *   - archive FAILED → `{ ci_job_id, upload_failed: true, reason }` — no `log_ref`; the
 *     mothership audits `CiLogUploadFailed`.
 *
 * That mutual exclusivity IS the write-half invariant: a report from the box that holds
 * the job leaves either a recorded `log_ref` or a logged upload-failure, and one payload
 * cannot carry both. A report from any OTHER box stamps nothing and is refused and
 * audited by the mothership (`apps/worker` `fleet/ci-log-refusal.ts`).
 */
export const CiLogReportSchema = z.union([
  z
    .object({
      /** Our `ci_jobs.id` — the row the mothership stamps (also the archived object's key basis). */
      ci_job_id: z.string().uuid(),
      /**
       * The object key the BOX says it archived the log under. NOT what is stored: the
       * mothership stamps `ci_jobs.log_ref` with the key it derives from `ci_job_id`
       * (`@developerz/storage` `ciLogKey`, `apps/worker` `fleet/ci-log.ts`) and audits a
       * wire value that disagrees. Kept on the wire, and left this loose, only so the
       * boxes already deployed still parse — the union is strict, so dropping or
       * tightening it would reject their reports.
       */
      log_ref: z.string().min(1),
      /** The inline `<2KB` failure-tail → `ci_jobs.log_excerpt` (may be empty for a clean run). */
      excerpt: z.string(),
    })
    .strict(),
  z
    .object({
      ci_job_id: z.string().uuid(),
      /** Discriminant — the archive/upload did NOT land, so there is no `log_ref` to record. */
      upload_failed: z.literal(true),
      /** Why it failed (presign 403, empty capture, PUT error) — audited, never recorded as a `log_ref`. */
      reason: z.string().min(1),
    })
    .strict(),
]);
export type CiLogReport = z.infer<typeof CiLogReportSchema>;

/** The upload-FAILED arm of {@link CiLogReport} (`CiLogUploadFailed` audit, no `log_ref`). */
export type CiLogUploadFailedReport = Extract<CiLogReport, { upload_failed: true }>;
/** The recorded (SUCCESS) arm of {@link CiLogReport} (`CiLogRecorded` audit + `log_ref` stamp). */
export type CiLogRecordedReport = Exclude<CiLogReport, { upload_failed: true }>;

/** Narrow a {@link CiLogReport}: `true` ⇒ the box could NOT archive the log (audit `CiLogUploadFailed`). */
export function isCiLogUploadFailed(report: CiLogReport): report is CiLogUploadFailedReport {
  return 'upload_failed' in report;
}
